import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { useMsal } from '@azure/msal-react'
import DashboardLayout from '@/components/layout/DashboardLayout'
import LoginPage from '@/pages/LoginPage'
import { InicioPage } from '@/pages/InicioPage'
import PlanificacionAvanzadaPage from '@/pages/PlanificacionAvanzadaPage'
import InventarioPage from '@/pages/InventarioPage'
import PlanProduccionPage from '@/pages/PlanProduccionPage'
import ProduccionDiariaPage from '@/pages/ProduccionDiariaPage'
import ProductionSequencePage from '@/pages/ProductionSequencePage'
import PedidosCompraPage    from '@/pages/PedidosCompraPage'
import PedidosVentasPage    from '@/pages/PedidosVentasPage'
import PedidoVentaDetallePage from '@/pages/PedidoVentaDetallePage'
import ReportsPage        from '@/pages/ReportsPage'
import CargasSalidaPage   from '@/pages/CargasSalidaPage'
import VentasSituacionPage from '@/pages/VentasSituacionPage'
import MuellesPage from '@/pages/MuellesPage'
import FichaCanvas       from '@/components/bom-captura/FichaCanvas'
import { ActivityLogger } from '@/components/ActivityLogger'
import { RequireBomAccess } from '@/components/auth/RequireBomAccess'
import { RequireEmpresaAll } from '@/components/auth/RequireEmpresaAll'
import { RequireProduccion } from '@/components/auth/RequireProduccion'
import { RequireModulo } from '@/components/auth/RequireModulo'
import { RequireReportsAccess } from '@/components/auth/RequireReportsAccess'
import { initApiAuth } from '@/services/api'
import { BrandSwitcher } from '@/branding/BrandSwitcher'

function InitAuth() {
  const { instance } = useMsal()
  useEffect(() => { initApiAuth(instance) }, [instance])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <InitAuth />
        <ActivityLogger />
        <Routes>
          <Route element={<DashboardLayout />}>
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="/home"                element={<InicioPage />} />
            {/* Compatibilidad: la antigua /home-v2 ahora vive en /home */}
            <Route path="/home-v2"             element={<Navigate to="/home" replace />} />
            {/* Compatibilidad: la antigua /vista-avanzada ahora vive en /gantt */}
            <Route path="/vista-avanzada"      element={<Navigate to="/gantt" replace />} />
            {/* Fabricación: no es para quien sólo compra y vende. Su empresa no
                tiene órdenes, así que estas pantallas saldrían vacías y parecerían
                rotas. El cierre real es el 403 del backend. */}
            <Route element={<RequireModulo modulo="produccion" />}>
            <Route element={<RequireProduccion />}>
              <Route path="/gantt"              element={<PlanificacionAvanzadaPage />} />
              {/* Secuencia: el resto de roles entra; el AVP, a la vista restringida (8 columnas) */}
              <Route path="/secuencia-produccion" element={<ProductionSequencePage />} />
              <Route path="/produccion-diaria"   element={<ProduccionDiariaPage />} />
              {/* El plan por formato además pide Empresa.All, que ya excluye a
                  las comerciales; el guard de fuera no sobra, lo declara. */}
              <Route element={<RequireEmpresaAll />}>
                <Route path="/plan-produccion"     element={<PlanProduccionPage />} />
              </Route>
            </Route>
            </Route>
            <Route element={<RequireModulo modulo="inventario" />}>
              <Route path="/inventario"        element={<InventarioPage />} />
            </Route>
            <Route element={<RequireModulo modulo="compras" />}>
              <Route path="/pedidos-compra"    element={<PedidosCompraPage />} />
            </Route>
            <Route element={<RequireModulo modulo="ventas" />}>
              <Route path="/pedidos-ventas"    element={<PedidosVentasPage />} />
              {/* Detalle de un pedido: modo foco, con la lista intacta detrás */}
              <Route path="/pedidos-ventas/:empresa/:salesid" element={<PedidoVentaDetallePage />} />
              {/* Situación de ventas (bloque 4): EN PRUEBAS, sólo en local. En un
                  build de producción la ruta no existe y cae en el 404. */}
              {import.meta.env.DEV && <Route path="/ventas-situacion" element={<VentasSituacionPage />} />}
            </Route>
            {/* Compatibilidad: Órdenes de servicio se fusionó en Pedidos de venta */}
            <Route path="/ordenes-servicio"    element={<Navigate to="/pedidos-ventas" replace />} />
            {/* Logística: qué sale y por dónde. Va por empresa, como el resto:
                las cargas son parte de la operativa de cualquiera que venda, y
                el cuadro de muelles ya marca los camiones ajenos como «fuera de
                ámbito», sin datos de negocio, para que nadie programe encima de
                un hueco ocupado. */}
            <Route element={<RequireModulo modulo="logistica" />}>
              <Route path="/cargas-salida"     element={<CargasSalidaPage />} />
            </Route>
            <Route element={<RequireModulo modulo="muelles" />}>
              <Route path="/muelles"           element={<MuellesPage />} />
            </Route>
            {/* Fichas técnicas: módulo `bom` Y acceso a tdis (Empresa.TDIS o
                Empresa.All). Son dos preguntas distintas —qué área y de qué
                empresa— y por eso van los dos guards, uno dentro del otro. */}
            <Route element={<RequireModulo modulo="bom" />}>
              <Route element={<RequireBomAccess />}>
                <Route path="/bom" element={<FichaCanvas />} />
                <Route path="/bom/:sistemaId/:empresa/:formula" element={<FichaCanvas />} />
              </Route>
            </Route>
            <Route element={<RequireModulo modulo="reports" />}>
            <Route element={<RequireReportsAccess />}>
              <Route path="/reports" element={<Navigate to="/reports/raw-materials" replace />} />
              <Route path="/reports/:report" element={<ReportsPage />} />
            </Route>
            </Route>
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Route>
        </Routes>
      </AuthenticatedTemplate>

      {import.meta.env.DEV && <BrandSwitcher />}
    </BrowserRouter>
  )
}
